import React from 'react';

/**
 * يحوّل تاريخ ISO إلى نص نسبي عربي:
 *  "متصل الآن" | "قبل دقيقة" | "قبل 5 دقائق" | "قبل ساعة" | "قبل يومين" ...
 */
export function getRelativeTimeArabic(isoString, isOnline) {
  if (isOnline) return 'متصل الآن';
  if (!isoString) return null;

  const then = new Date(isoString);
  if (isNaN(then.getTime())) return null;

  const diffSec = Math.floor((Date.now() - then.getTime()) / 1000);
  if (diffSec < 60) return 'متصل الآن';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) {
    if (diffMin === 1) return 'قبل دقيقة';
    if (diffMin === 2) return 'قبل دقيقتين';
    if (diffMin <= 10) return `قبل ${diffMin} دقائق`;
    return `قبل ${diffMin} دقيقة`;
  }
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) {
    if (diffHr === 1) return 'قبل ساعة';
    if (diffHr === 2) return 'قبل ساعتين';
    if (diffHr <= 10) return `قبل ${diffHr} ساعات`;
    return `قبل ${diffHr} ساعة`;
  }
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) {
    if (diffDay === 1) return 'قبل يوم';
    if (diffDay === 2) return 'قبل يومين';
    if (diffDay <= 10) return `قبل ${diffDay} أيام`;
    return `قبل ${diffDay} يوم`;
  }
  const diffMonth = Math.floor(diffDay / 30);
  if (diffMonth < 12) {
    if (diffMonth === 1) return 'قبل شهر';
    if (diffMonth === 2) return 'قبل شهرين';
    return `قبل ${diffMonth} أشهر`;
  }
  const diffYear = Math.floor(diffMonth / 12);
  if (diffYear === 1) return 'قبل سنة';
  return `قبل ${diffYear} سنوات`;
}

/**
 * شارة حالة الاتصال:
 *  - أخضر متوهج "متصل الآن" إذا is_online === true
 *  - رمادي خفيف "آخر ظهور قبل X" إذا غير متصل وlast_seen موجود
 *  - لا شيء إذا لم يكن هناك أي بيانات
 */
export default function OnlineStatus({ isOnline, lastSeen, size = 'sm' }) {
  const label = getRelativeTimeArabic(lastSeen, isOnline);
  if (!label) return null;

  const padding = size === 'lg' ? '6px 12px' : '3px 9px';
  const fontSize = size === 'lg' ? '13px' : '11px';
  const dotSize = size === 'lg' ? '8px' : '7px';

  if (isOnline) {
    return (
      <span
        data-testid="online-status-online"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding,
          background: 'rgba(16, 185, 129, 0.13)',
          color: '#059669',
          borderRadius: '999px',
          fontSize,
          fontWeight: 700,
          border: '1px solid rgba(16, 185, 129, 0.3)',
        }}
      >
        <span
          style={{
            display: 'inline-block',
            width: dotSize,
            height: dotSize,
            background: '#10B981',
            borderRadius: '50%',
            boxShadow: '0 0 0 0 rgba(16, 185, 129, 0.6)',
            animation: 'onlinePulse 1.8s ease-in-out infinite',
          }}
        />
        متصل الآن
        <style>{`
          @keyframes onlinePulse {
            0%   { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.55); }
            70%  { box-shadow: 0 0 0 6px rgba(16, 185, 129, 0); }
            100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
          }
        `}</style>
      </span>
    );
  }

  return (
    <span
      data-testid="online-status-offline"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        padding,
        background: 'rgba(148, 163, 184, 0.13)',
        color: '#64748B',
        borderRadius: '999px',
        fontSize,
        fontWeight: 600,
        border: '1px solid rgba(148, 163, 184, 0.25)',
      }}
    >
      <span
        style={{
          display: 'inline-block',
          width: dotSize,
          height: dotSize,
          background: '#94A3B8',
          borderRadius: '50%',
        }}
      />
      آخر ظهور {label.replace('متصل الآن', '')}
    </span>
  );
}
